import { useEffect } from 'react';

import { usePrevious } from '../../hooks/usePrevious';
import { Guid } from '../../store/model/guid';
import { areAnyItemsUnlockedSelector } from '../../store/reducers/student/tracking/itemsTrackingSelectors';
import { useStudentSelector } from '../../store/selectors/useStudentSelector';
import { useLinearModeDataSelector } from '../RestrictMenu/LinearMode/useLinearModeDataSelector';
import { subtractArray } from '@ael/core-api';

interface UseUnlockedItemsObserverParams {
  isLinearModeOn: boolean;
  onUnlocked: (newlyUnlockedItemIds: Guid[]) => void;
  onReset: (unlockedItemIds: Guid[]) => void;
}

export const useUnlockedItemsObserver = ({ onUnlocked, onReset, isLinearModeOn }: UseUnlockedItemsObserverParams) => {
  const linearModeData = useLinearModeDataSelector(isLinearModeOn);
  const areAnyItemsUnlocked = useStudentSelector(areAnyItemsUnlockedSelector);
  const isReset = !areAnyItemsUnlocked;

  const previousIsReset = usePrevious(isReset);
  const previousLockStatus = usePrevious(linearModeData);

  useEffect(() => {
    if (!linearModeData.isInitialized) {
      return;
    }

    const previousUnlocked = previousLockStatus?.getAllUnlocked() ?? [];
    const newlyUnlockedItems = subtractArray(linearModeData.getAllUnlocked(), previousUnlocked);
    if (newlyUnlockedItems.length > 0) {
      onUnlocked(newlyUnlockedItems);
    }

    const wasReset = previousLockStatus ? !previousIsReset && isReset : false;

    if (wasReset) {
      onReset(linearModeData.getAllUnlocked());
    }
  }, [linearModeData, onReset, onUnlocked, previousLockStatus, isReset, previousIsReset]);
};
